import React, { useCallback, useContext } from "react";
import { Menu, Transition } from "@headlessui/react";
import { SectionTemplateType } from "../../../../../src/js/polaris/util/privacyCenterUtil";
import { useHistory } from "react-router";
import { Routes } from "../../../../../src/js/polaris/root/routes";
import clsx from "clsx";
import {
  BuildingStorefrontIcon,
  QuestionMarkCircleIcon,
  ArrowRightEndOnRectangleIcon,
} from "@heroicons/react/24/solid";
import { useActionRequest } from "../../../../../src/js/common/hooks/api";
import { Api } from "../../../../../src/js/common/service/Api";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { selfActions } from "../../../../../src/js/common/reducers/selfSlice";
import { OmniBarContext } from "../../../../../src/js/common/hooks/useOmniBar";
import { switchActiveOrganization } from "../../../../../src/js/polaris/util/organizationNavigation";
import {
  Error as RequestsError,
  Success as RequestsSuccess,
} from "../../../../../src/js/polaris/copy/requests";
import { useMenu } from "../../../../../src/js/polaris/components/layout/nav/UserNavItem";
import { truncateWithEllipsis } from "../../../../../src/js/polaris/util/text";
import { clearEditModeFlag } from "../../../../../src/js/polaris/edit-mode/editModeState";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export interface NewSectionDropdownTemplate {
  label: string;
  sectionType: SectionTemplateType;
  templateGroup: "Core" | "International";
}

export default function OnboardingOrganizationDropdown({
  orgName,
  userDisplayName,
  showSettings = true,
}: {
  orgName: string;
  userDisplayName: string;
  showSettings?: boolean;
}) {
  const history = useHistory();
  const dispatchRedux = useDispatch();
  const queryClient = useQueryClient();
  const { openOmniBar } = useContext(OmniBarContext);
  const menu = useMenu("nav-profile-popover");

  const { fetch: updateActiveOrganizationSelf } = useActionRequest({
    api: switchActiveOrganization,
    messages: {
      defaultError: RequestsError.SetActiveOrganization,
      success: RequestsSuccess.SetActiveOrganization,
    },
    notifyError: true,
    notifySuccess: false,
  });

  const switchOrg = useCallback(
    async (e: any) => {
      e.preventDefault();
      openOmniBar({
        onClose: (data) => {
          if (data?.organization) {
            clearEditModeFlag();
            updateActiveOrganizationSelf(data.organization);
          }
          menu.close();
        },
      });
    },
    [menu, openOmniBar, updateActiveOrganizationSelf],
  );

  const { fetch: logout } = useActionRequest({
    api: async () => await Api.login.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries(["self"]);
      dispatchRedux(selfActions.purge());
    },
  });

  return (
    <Menu
      as="div"
      className="relative inline-block min-w-[224px] text-left"
      style={{ zIndex: 110 }}
    >
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              className={clsx(
                "inline-flex w-full max-w-[30ch] overflow-hidden justify-center rounded-[6px] p-[8px] items-center",
                open && "bg-primary-100",
              )}
            >
              <BuildingStorefrontIcon
                className={clsx(
                  "h-[24px] w-[24px]",
                  open ? "text-primary-700" : "text-neutral-600",
                )}
              />
              <div
                className={clsx(
                  "ml-[12px] text-[14px] font-medium",
                  open ? "text-primary-600" : "text-neutral-700",
                )}
              >
                {truncateWithEllipsis(orgName, 30)}
              </div>
            </Menu.Button>
          </div>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute z-10 mt-2 origin-top-right rounded-md bg-white shadow-lg focus:outline-none w-full">
              <div className="px-[16px] py-[8px] text-[14px] text-neutral-800 font-medium break-all">
                {userDisplayName}
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active, close }) => (
                    <div
                      className={classNames(
                        active ? "bg-neutral-100 text-gray-900" : "text-[rgba(0,0,0,0.87)]",
                        "cursor-pointer",
                        "px-4 py-2 flex items-center justify-between",
                        "break-words",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        switchOrg(e);
                        close();
                      }}
                    >
                      <div className="flex items-center">
                        <BuildingStorefrontIcon className="h-[20px] w-[20px] flex-shrink-0 text-neutral-500" />
                        <div className="ml-[12px]">{orgName}</div>
                      </div>
                      <div className="text-primary-600 text-[14px] font-medium flex-shrink-0 ml-[6px]">
                        Switch
                      </div>
                    </div>
                  )}
                </Menu.Item>
                {showSettings && (
                  <Menu.Item>
                    {({ active, close }) => (
                      <div
                        className={classNames(
                          active ? "bg-neutral-100 text-gray-900" : "text-[rgba(0,0,0,0.87)]",
                          "cursor-pointer",
                          "px-4 py-2 flex items-center",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          history.push(Routes.getCompliant.settings.root);
                          close();
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ height: "20px", color: "#918CA1" }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.828 2.25c-.916 0-1.699.663-1.85 1.567l-.091.549a.798.798 0 0 1-.517.608 7.45 7.45 0 0 0-.478.198.798.798 0 0 1-.796-.064l-.453-.324a1.875 1.875 0 0 0-2.416.2l-.243.243a1.875 1.875 0 0 0-.2 2.416l.324.453a.798.798 0 0 1 .064.796 7.448 7.448 0 0 0-.198.478.798.798 0 0 1-.608.517l-.55.092a1.875 1.875 0 0 0-1.566 1.849v.344c0 .916.663 1.699 1.567 1.85l.549.091c.281.047.508.25.608.517.06.162.127.321.198.478a.798.798 0 0 1-.064.796l-.324.453a1.875 1.875 0 0 0 .2 2.416l.243.243c.648.648 1.67.733 2.416.2l.453-.324a.798.798 0 0 1 .796-.064c.157.071.316.137.478.198.267.1.47.327.517.608l.092.55c.15.903.932 1.566 1.849 1.566h.344c.916 0 1.699-.663 1.85-1.567l.091-.549a.798.798 0 0 1 .517-.608 7.52 7.52 0 0 0 .478-.198.798.798 0 0 1 .796.064l.453.324a1.875 1.875 0 0 0 2.416-.2l.243-.243c.648-.648.733-1.67.2-2.416l-.324-.453a.798.798 0 0 1-.064-.796c.071-.157.137-.316.198-.478.1-.267.327-.47.608-.517l.55-.091a1.875 1.875 0 0 0 1.566-1.85v-.344c0-.916-.663-1.699-1.567-1.85l-.549-.091a.798.798 0 0 1-.608-.517 7.507 7.507 0 0 0-.198-.478.798.798 0 0 1 .064-.796l.324-.453a1.875 1.875 0 0 0-.2-2.416l-.243-.243a1.875 1.875 0 0 0-2.416-.2l-.453.324a.798.798 0 0 1-.796.064 7.462 7.462 0 0 0-.478-.198.798.798 0 0 1-.517-.608l-.091-.55a1.875 1.875 0 0 0-1.85-1.566h-.344ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="ml-[12px]">Settings</div>
                      </div>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active, close }) => (
                    <div
                      className={classNames(
                        active ? "bg-neutral-100 text-gray-900" : "text-[rgba(0,0,0,0.87)]",
                        "cursor-pointer",
                        "px-4 py-2 flex items-center",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        (window as any).Beacon("open");
                        close();
                      }}
                    >
                      <QuestionMarkCircleIcon className="h-[20px] w-[20px] text-neutral-500" />
                      <div className="ml-[12px]">Help</div>
                    </div>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active, close }) => (
                    <div
                      className={classNames(
                        active ? "bg-neutral-100 text-gray-900" : "text-[rgba(0,0,0,0.87)]",
                        "cursor-pointer",
                        "px-4 py-2 flex items-center",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logout();
                        close();
                      }}
                    >
                      <ArrowRightEndOnRectangleIcon className="h-[20px] w-[20px] text-neutral-500" />
                      <div className="ml-[12px]">Logout</div>
                    </div>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}
